import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { requestSummarizeAll, getSummarizeAllStatus, requestOrderSummary } from "../services/orderSummaryApi";

/**
 * SummarizeAllModal — centered popup modal for summarizing all orders in a case concurrently.
 * Rendered via portal into document.body with a dimmed backdrop, same as AISummaryModal.
 */
function SummarizeAllModal({ isOpen, onClose, orders, courtType }) {
  const [status, setStatus] = useState("idle");
  const [summaries, setSummaries] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [failureReason, setFailureReason] = useState(null);
  const [jobId, setJobId] = useState(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const startPolling = useCallback((jid) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const res = await getSummarizeAllStatus(jid);
        if (!mountedRef.current) return;

        setSummaries(res.summaries || []);
        setCompletedCount(res.completedCount || 0);
        setTotalCount(res.totalCount || 0);

        if (res.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStatus("completed");
        } else if (res.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setFailureReason(res.failureReason || "unknown");
          setStatus("failed");
        }
      } catch (err) {
        console.error("Polling error (SummarizeAll):", err);
      }
    }, 2000);
  }, []);

  const handleRetrySingle = async (orderId) => {
    const originalOrder = orders.find(o => o.orderId === orderId);
    if (!originalOrder) return;
    
    // Optimistically update the UI to show processing state
    setSummaries(prev => prev.map(item => 
      item.orderId === orderId ? { ...item, status: 'processing', is_error: false } : item
    ));
    
    // Restart polling if the overall batch was already completed
    if (status === 'completed' || status === 'failed') {
      setStatus('loading');
      startPolling(jobId);
    }
    
    try {
      await requestOrderSummary({
        orderId: originalOrder.orderId,
        orderLink: originalOrder.orderLink || undefined,
        pdfProxyUrl: originalOrder.pdfProxyUrl || undefined,
        pdfStorageUrl: originalOrder.pdfStorageUrl || undefined,
      });
    } catch (err) {
      console.error("Single retry failed", err);
      // Let the next poll cycle reflect the error
    }
  };

  const initiateSummarization = useCallback(async () => {
    if (!orders || orders.length === 0) return;
    setStatus("loading");
    
    // Seed initial summaries state so we can show "Waiting..." immediately for each order
    const initialSummaries = orders.map((o) => ({
      orderId: o.orderId,
      status: "pending"
    }));
    setSummaries(initialSummaries);
    
    setCompletedCount(0);
    setTotalCount(orders.length);
    setFailureReason(null);
    setJobId(null);

    try {
      const payload = {
        orders: orders.map((o) => ({
          orderId: o.orderId,
          orderLink: o.orderLink || undefined,
          pdfProxyUrl: o.pdfProxyUrl || undefined,
          pdfStorageUrl: o.pdfStorageUrl || undefined,
        })),
      };

      const res = await requestSummarizeAll(payload);
      if (!mountedRef.current) return;

      setJobId(res.jobId);
      if (res.status === "completed") {
        setStatus("completed");
      } else if (res.status === "failed") {
        setFailureReason(res.failureReason || "unknown");
        setStatus("failed");
      } else {
        startPolling(res.jobId);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      console.error("Batch summary request error:", err);
      setFailureReason("llm_failed");
      setStatus("failed");
    }
  }, [orders, startPolling]);

  useEffect(() => {
    if (isOpen) {
      initiateSummarization();
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setStatus("idle");
    }
  }, [isOpen, initiateSummarization]);

  if (!isOpen) return null;

  // Build a lookup map of labels (e.g. "BAIL APPLN./1799/2026 — 08-05-2026") by orderId
  const getOrderLabel = (orderId) => {
    const o = orders.find((ord) => ord.orderId === orderId);
    if (!o) return "Order";
    if (o.orderLabel) return o.orderLabel;

    // Fallback if orderLabel is missing
    return o.orderId;
  };

  // The backend prefixes each segment with "**<case no>**"; drop the useless
  // placeholder headers ("**UNKNOWN**", "**ORDER_n_UNRESOLVED**") it emits
  // when no case number could be detected in the PDF.
  const cleanSummaryText = (text) =>
    text ? text.replace(/^\*\*(?:UNKNOWN|ORDER_\d+_UNRESOLVED)\*\*\s*\n?/gm, "").trim() : text;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      style={{ animation: "summarizeAllFadeIn 0.2s ease-out" }}
      onClick={onClose}
    >
      <style>{`
        @keyframes summarizeAllFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes summarizeAllPopIn {
          from { opacity: 0; transform: scale(0.96) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      <div
        className="bg-white rounded-2xl border border-gray-200 shadow-2xl flex flex-col overflow-hidden w-full max-w-xl"
        style={{ maxHeight: "80vh", animation: "summarizeAllPopIn 0.25s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-purple-500" />
            <div>
              <h2 className="text-gray-900 font-semibold text-base">AI Order Summaries</h2>
              <p className="text-xs text-gray-500">Summarizing all orders for this case</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex-1 overflow-y-auto">
          {/* Progress / Status */}
          <div className="mb-4 flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
            <span className="text-sm text-gray-600">
              Completed {completedCount} of {totalCount}
            </span>
            {status === "loading" || (status === "pending" || pollRef.current) ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
                <span className="text-sm text-purple-600">Processing...</span>
              </div>
            ) : status === "completed" ? (
              <span className="text-sm text-green-600">Done</span>
            ) : (
              <span className="text-sm text-red-500">Failed</span>
            )}
          </div>

          {/* List of summaries */}
          <div className="flex flex-col gap-4">
            {summaries.map((s, idx) => (
              <div
                key={s.orderId || idx}
                className={`rounded-xl p-4 border ${
                  s.status === 'failed' || s.is_error
                    ? 'bg-red-50 border-red-200'
                    : s.status === 'completed'
                    ? 'bg-gray-50 border-gray-200'
                    : 'bg-white border-gray-200 opacity-60'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {(s.status === 'failed' || s.is_error) && <span className="text-red-500 text-sm">⚠️</span>}
                  {s.status === 'processing' && <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />}
                  <h4 className={`${(s.status === 'failed' || s.is_error) ? 'text-red-600' : 'text-gray-900'} font-semibold text-sm`}>
                    Order {idx + 1} ({getOrderLabel(s.orderId)})
                  </h4>
                </div>

                {s.status === 'completed' && s.summary ? (
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                    {cleanSummaryText(s.summary)}
                  </p>
                ) : (s.status === 'failed' || s.is_error) ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-red-600/90 text-sm leading-relaxed whitespace-pre-line">
                      {cleanSummaryText(s.summary) || "Summary generation failed for this order. It may be a scanned image or unavailable."}
                    </p>
                    <button
                      onClick={() => handleRetrySingle(s.orderId)}
                      className="mt-1 self-start px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-xs rounded-lg transition-colors flex items-center justify-center gap-1.5"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Retry
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm italic">Waiting for AI summary...</p>
                )}
              </div>
            ))}
          </div>

          {/* Global error */}
          {status === "failed" && (!summaries || summaries.length === 0) && (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                <span className="text-2xl">⚠️</span>
              </div>
              <p className="text-gray-600 text-sm text-center max-w-sm">
                Batch summary generation failed to start. {failureReason}
              </p>
              <button
                onClick={initiateSummarization}
                className="mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Retry All
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SummarizeAllModal;
